// paymentsService.ts
import api from './apiClient.ts';

export interface TravelerBalancesParams {
  branch_id?: number | string;
  start_date?: string;
  end_date?: string;
  page?: number;
  per_page?: number;
}

export interface GroupDetailsParams {
  branch_id?: number | string;
  gr_id?: number | string;
  tr_id?: number | string;
  paytype?: 'cash' | 'installments';
  page?: number;
  per_page?: number;
}

export interface InstallmentsParams {
  branch_id?: number | string;
  gr_id?: number | string;
  tr_id?: number | string;
  min_amount?: number;
  max_amount?: number;
  start_date?: string;
  end_date?: string;
  page?: number;
  per_page?: number;
  sort?: string;
}

export interface ScheduleParams {
  gr_id?: number | string;
  tr_id?: number | string;
  branch_id?: number | string;
  paytype?: 'cash' | 'installments';
  as_of?: string; // YYYY-MM-DD
  suggest?: boolean;
  window_days?: number;
  overdue?: boolean;
  min_days_overdue?: number;
  flatten?: boolean; // default false
  include_paid?: boolean;
  page?: number;
  per_page?: number;
}

export interface InstallmentCreatePayload {
  gr_id: number | string;
  tr_id: number | string;
  amount: number;
  paydate?: string; // YYYY-MM-DD
  notes?: string;
  vault?: string; // optional vault label
  branch_id?: number | string;
}

export interface InstallmentUpdatePayload {
  amount?: number;
  paydate?: string; // YYYY-MM-DD
  notes?: string;
  vault?: string;
}

export interface BatchCreateTravelerPayload {
  name: string;
  phone1?: string;
  phone2?: string;
  passport?: string;
  agent?: string;
  agent_money?: number;
  paytype: 'cash' | 'installments';
  cash?: number; // if cash paytype
  total_price?: number; // if installments paytype
  gr_interval?: number; // months
  price?: number; // installment amount
}

export interface BatchCreateGroupPayload {
  request_token: string;
  group: {
    gr_notes?: string;
    plan_start_date?: string; // YYYY-MM-DD
    branch_id?: number | string;
    vault?: string;
  };
  travelers: BatchCreateTravelerPayload[];
}

// New: Travelers & Groups list params
export interface TravelerParams {
  branch_id?: number | string;
  search?: string;
  page?: number;
  per_page?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
}

export interface GroupParams {
  branch_id?: number | string;
  search?: string;
  page?: number;
  per_page?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
}

const paymentsService = {
  // Reports
  async getTravelerBalances(params: TravelerBalancesParams = {}) {
    const res = await api.get('/v1/reports/travelers/balances', { params });
    return res.data;
  },

  // Group Details (payment plan info per traveler)
  async getGroupDetails(params: GroupDetailsParams = {}) {
    const res = await api.get('/v1/group-details', { params });
    return res.data;
  },

  // Installments (payment history)
  async getInstallments(params: InstallmentsParams = {}) {
    const res = await api.get('/v1/installments', { params });
    return res.data;
  },

  async createInstallment(payload: InstallmentCreatePayload) {
    const res = await api.post('/v1/installments', payload);
    return res.data;
  },

  async updateInstallment(id: number | string, payload: InstallmentUpdatePayload) {
    const res = await api.put(`/v1/installments/${id}`, payload);
    return res.data;
  },

  async deleteInstallment(id: number | string) {
    const res = await api.delete(`/v1/installments/${id}`);
    return res.data;
  },

  // Installment schedule and summaries
  async getInstallmentSchedule(params: ScheduleParams = {}) {
    const res = await api.get('/v1/installments/schedule', { params });
    return res.data;
  },

  // Payments meta & CRUD (if needed elsewhere)
  async getPayments(params: Record<string, any> = {}) {
    const res = await api.get('/v1/payments', { params });
    return res.data;
  },
  async updatePayment(id: number | string, payload: Record<string, any>) {
    const res = await api.put(`/v1/payments/${id}`, payload);
    return res.data;
  },
  async deletePayment(id: number | string) {
    const res = await api.delete(`/v1/payments/${id}`);
    return res.data;
  },
  async getPaymentsMeta() {
    const res = await api.get('/v1/payments/meta');
    return res.data;
  },

  // Batch creation for randomized test data
  async batchCreateGroups(payload: BatchCreateGroupPayload) {
    const res = await api.post('/v1/groups/batch', payload);
    return res.data;
  },

  // New: Travelers list
  async getTravelers(params: TravelerParams = {}) {
    const res = await api.get('/v1/travelers', { params });
    return res.data;
  },

  // New: Groups list
  async getGroups(params: GroupParams = {}) {
    const res = await api.get('/v1/groups', { params });
    return res.data;
  },
};

export default paymentsService;